// PhoneVerification2.jsx
"use client"
import { useState, useEffect, useCallback, useRef } from "react"

import {
    Box,
    Button,
    Input,
    Text,
    VStack,
    HStack,
    Card,
    CardBody,
    Heading,
    Link,
    FormControl,
    Badge,
    Progress,
    Icon,
    Container,
    useColorModeValue,
    InputGroup,
    InputLeftElement,
    Circle,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Spinner,
    Divider,
} from "@chakra-ui/react"
import { auth, setupRecaptcha, signInWithPhoneNumber } from "../../../firebaseConfig"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { CheckPhoneVerifyAsync } from "../Slice/PhoneVerified/CheckPhoneVerifySlice"
import {
    checkEmailVerificationPublicAsync,
    generateEmailVerificationPublicAsync,
    resetEmailVerificationState,
} from "../Slice/EmailVerificationSignup/EmailVerificationSignupSlice"
import {
    checkPhoneVerificationPublicAsync,
    resetPhoneVerificationState,
} from "../Slice/PhoneVerificationSignup/PhoneVerificationSlice"
import {
    fetchBasicDetailsBySingleParamAsync,
    selectBasicDetailsBySingleParam,
} from "../../profile/BasicDetailsSingleParam/BasicDetailsSingleParamSlice"
import { fetchCookie } from "../../common/FetchCookie"

// Resend OTP cooldown time in seconds
const RESEND_COOLDOWN = 120

// Minimal icons
const PhoneIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path
            fill="currentColor"
            d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
        />
    </Icon>
)

const EmailIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path
            fill="currentColor"
            d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
        />
    </Icon>
)

const CheckIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </Icon>
)

const ShieldIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path
            fill="currentColor"
            d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.5C15.4,11.5 16,12.4 16,13V16C16,17.4 15.4,18 14.8,18H9.2C8.6,18 8,17.4 8,16V13C8,12.4 8.6,11.5 9.2,11.5V10C9.2,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,10V11.5H13.5V10C13.5,8.7 12.8,8.2 12,8.2Z"
        />
    </Icon>
)

const RefreshIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path
            fill="currentColor"
            d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
        />
    </Icon>
)

const WarningIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path fill="currentColor" d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </Icon>
)

const ArrowRightIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path fill="currentColor" d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
    </Icon>
)

export default function PhoneVerification2() {
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const redirectTo = location.state?.redirectTo || "/candidate-profile"

    const [mobile, setMobile] = useState(location.state?.phone || "")
    const [email, setEmail] = useState(location.state?.email || "")
    const [otp, setOtp] = useState("")
    const [emailOtp, setEmailOtp] = useState("")
    const [confirmationResult, setConfirmationResult] = useState(null)
    const [error, setError] = useState("")
    const [emailError, setEmailError] = useState("")
    const [loading, setLoading] = useState(false)
    const [successMessage, setSuccessMessage] = useState("")
    const [emailSuccessMessage, setEmailSuccessMessage] = useState("")
    const [isEmailVerified, setIsEmailVerified] = useState(false)
    const [isPhoneVerified, setIsPhoneVerified] = useState(false)
    const [otpSent, setOtpSent] = useState(false)

    // LOCAL state for email OTP sent (more reliable than Redux for UI)
    const [emailOtpSent, setEmailOtpSent] = useState(false)
    const [emailVerifying, setEmailVerifying] = useState(false)

    // Auth type state - 'form' or 'oauth'
    const [authType, setAuthType] = useState(null)
    const [isAuthTypeLoaded, setIsAuthTypeLoaded] = useState(false)

    // Phone number already exists state (for OAuth)
    const [phoneAlreadyExists, setPhoneAlreadyExists] = useState(false)

    // Resend OTP timer states
    const [phoneResendTimer, setPhoneResendTimer] = useState(0)
    const [emailResendTimer, setEmailResendTimer] = useState(0)
    const [canResendPhoneOtp, setCanResendPhoneOtp] = useState(false)
    const [canResendEmailOtp, setCanResendEmailOtp] = useState(false)

    // Use refs for timer intervals to prevent memory leaks
    const phoneTimerRef = useRef(null)
    const emailTimerRef = useRef(null)

    // Get basic details from Redux
    const basicDetails = useSelector(selectBasicDetailsBySingleParam)

    // Phone verification state from Redux
    const {
        loading: phoneLoading,
        success: phoneVerifiedRedux,
        error: phoneReduxError,
    } = useSelector((state) => state.phoneVerificationPublic || {})

    // Email verification state from Redux
    const {
        loading: emailLoading,
        otpSent: emailOtpSentRedux,
        success: emailVerifiedRedux,
        error: emailReduxError,
    } = useSelector((state) => state.emailVerification || {})

    // Color scheme
    const bgColor = useColorModeValue("gray.50", "gray.900")
    const cardBg = useColorModeValue("white", "gray.800")
    const borderColor = useColorModeValue("gray.200", "gray.600")
    const textColor = useColorModeValue("gray.800", "white")
    const mutedTextColor = useColorModeValue("gray.600", "gray.400")

    // Phone resend timer effect - FIXED
    useEffect(() => {
        // Clear any existing interval
        if (phoneTimerRef.current) {
            clearInterval(phoneTimerRef.current)
            phoneTimerRef.current = null
        }

        if (phoneResendTimer > 0) {
            phoneTimerRef.current = setInterval(() => {
                setPhoneResendTimer((prevTimer) => {
                    if (prevTimer <= 1) {
                        setCanResendPhoneOtp(true)
                        if (phoneTimerRef.current) {
                            clearInterval(phoneTimerRef.current)
                            phoneTimerRef.current = null
                        }
                        return 0
                    }
                    return prevTimer - 1
                })
            }, 1000)
        }

        return () => {
            if (phoneTimerRef.current) {
                clearInterval(phoneTimerRef.current)
                phoneTimerRef.current = null
            }
        }
    }, [phoneResendTimer])

    // Email resend timer effect - FIXED
    useEffect(() => {
        // Clear any existing interval
        if (emailTimerRef.current) {
            clearInterval(emailTimerRef.current)
            emailTimerRef.current = null
        }

        if (emailResendTimer > 0) {
            emailTimerRef.current = setInterval(() => {
                setEmailResendTimer((prevTimer) => {
                    if (prevTimer <= 1) {
                        setCanResendEmailOtp(true)
                        if (emailTimerRef.current) {
                            clearInterval(emailTimerRef.current)
                            emailTimerRef.current = null
                        }
                        return 0
                    }
                    return prevTimer - 1
                })
            }, 1000)
        }

        return () => {
            if (emailTimerRef.current) {
                clearInterval(emailTimerRef.current)
                emailTimerRef.current = null
            }
        }
    }, [emailResendTimer])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (phoneTimerRef.current) clearInterval(phoneTimerRef.current)
            if (emailTimerRef.current) clearInterval(emailTimerRef.current)
        }
    }, [])

    // Start phone resend timer - FIXED
    const startPhoneResendTimer = useCallback(() => {
        // Clear existing timer first
        if (phoneTimerRef.current) {
            clearInterval(phoneTimerRef.current)
            phoneTimerRef.current = null
        }
        setCanResendPhoneOtp(false)
        setPhoneResendTimer(RESEND_COOLDOWN)
    }, [])

    // Start email resend timer - FIXED
    const startEmailResendTimer = useCallback(() => {
        // Clear existing timer first
        if (emailTimerRef.current) {
            clearInterval(emailTimerRef.current)
            emailTimerRef.current = null
        }
        setCanResendEmailOtp(false)
        setEmailResendTimer(RESEND_COOLDOWN)
    }, [])

    // Format timer display
    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, "0")}`
    }

    // Fetch auth type from cookie on mount
    useEffect(() => {
        const getAuthType = async () => {
            try {
                const userData = await fetchCookie()
                const registerType = userData?.user?.register_type || userData?.register_type

                // Handle both 'oauth' and 'google' as OAuth type
                if (registerType === "oauth" || registerType === "google") {
                    setAuthType("oauth")
                } else {
                    setAuthType("form")
                }

                // Also prefill email from cookie if not already set
                if (!email && userData?.user?.email) {
                    setEmail(userData.user.email)
                }

                // Prefill phone from cookie if available (for form type)
                if (!mobile && userData?.user?.phone) {
                    setMobile(String(userData.user.phone))
                }
            } catch (error) {
                console.error("Error fetching auth type:", error)
                setAuthType("form") // Default to 'form' on error
            } finally {
                setIsAuthTypeLoaded(true)
            }
        }
        getAuthType()
    }, []) // Remove email and mobile from dependencies to prevent re-runs

    // Setup recaptcha when component mounts
    useEffect(() => {
        const initializeRecaptcha = () => {
            try {
                setupRecaptcha()
            } catch (error) {
                console.error("Error initializing recaptcha:", error)
            }
        }
        const timer = setTimeout(initializeRecaptcha, 100)
        return () => {
            clearTimeout(timer)
            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear()
                window.recaptchaVerifier = null
            }
        }
    }, [])

    // Reset verification states and fetch profile on component mount
    useEffect(() => {
        dispatch(resetEmailVerificationState())
        dispatch(resetPhoneVerificationState())
        dispatch(fetchBasicDetailsBySingleParamAsync())
    }, [dispatch])

    // Prefill email and phone from basicDetails
    useEffect(() => {
        if (basicDetails) {
            if (basicDetails.email && !email) {
                setEmail(basicDetails.email)
            }
            // For form signup, prefill phone from basicDetails
            if (authType === "form" && basicDetails.phone && !mobile) {
                setMobile(String(basicDetails.phone))
            }
            // Check if already verified from API response
            if (basicDetails.email_verify === 1) {
                setIsEmailVerified(true)
            }
            if (basicDetails.phone_verify === 1) {
                setIsPhoneVerified(true)
            }
        }
    }, [basicDetails, authType]) // Remove email and mobile from dependencies

    // Handle phone verification success
    useEffect(() => {
        if (phoneVerifiedRedux) {
            setIsPhoneVerified(true)
            setSuccessMessage("Phone verification successful!")
            setOtp("")
            setTimeout(() => {
                navigate(redirectTo, { replace: true })
            }, 1500)
        }
    }, [phoneVerifiedRedux, navigate, redirectTo])

    // Handle phone verification error
    useEffect(() => {
        if (phoneReduxError) {
            setError(phoneReduxError)
        }
    }, [phoneReduxError])

    // Handle email verification success from Redux
    useEffect(() => {
        if (emailVerifiedRedux) {
            setIsEmailVerified(true)
            setEmailSuccessMessage("Email verification successful!")
            setEmailOtp("")
            setEmailOtpSent(false)
        }
    }, [emailVerifiedRedux])

    // Handle email verification error from Redux
    useEffect(() => {
        if (emailReduxError) {
            setEmailError(emailReduxError)
        }
    }, [emailReduxError])

    // Sync Redux emailOtpSentRedux with local state
    useEffect(() => {
        if (emailOtpSentRedux && !emailOtpSent) {
            setEmailOtpSent(true)
        }
    }, [emailOtpSentRedux])

    const formatPhoneNumber = (phone) => {
        if (!phone) return ""
        const cleaned = phone.toString().replace(/\D/g, "")
        if (phone.toString().startsWith("+91")) {
            return phone.toString()
        }
        if (cleaned.length === 10) {
            return `+91${cleaned}`
        }
        if (cleaned.startsWith("91") && cleaned.length === 12) {
            return `+${cleaned}`
        }
        return `+91${cleaned}`
    }

    const validatePhoneNumber = (phone) => {
        if (!phone) return false
        const cleaned = phone.toString().replace(/\D/g, "")
        return cleaned.length === 10 || (cleaned.length === 12 && cleaned.startsWith("91"))
    }

    // Helper function to send OTP via Firebase
    const sendOtpViaFirebase = async (phoneNumber) => {
        try {
            if (!window.recaptchaVerifier) {
                setupRecaptcha()
                await new Promise((resolve) => setTimeout(resolve, 500))
            }

            const appVerifier = window.recaptchaVerifier
            if (!appVerifier) {
                throw new Error("Recaptcha verifier not initialized")
            }

            const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier)
            setConfirmationResult(confirmation)
            setSuccessMessage("OTP sent successfully!")
            setOtpSent(true)
            setPhoneAlreadyExists(false)
            startPhoneResendTimer() // Start the resend timer
        } catch (otpError) {
            console.error("OTP Error:", otpError)
            if (otpError.code === "auth/invalid-phone-number") {
                setError("Invalid phone number format. Please check and try again.")
            } else if (otpError.code === "auth/too-many-requests") {
                setError("Too many requests. Please try again later.")
            } else if (otpError.code === "auth/quota-exceeded") {
                setError("SMS quota exceeded. Please try again later.")
            } else {
                setError("Error sending OTP. Please try again.")
            }

            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear()
                window.recaptchaVerifier = null
            }

            try {
                setupRecaptcha()
            } catch (setupError) {
                console.error("Error reinitializing recaptcha:", setupError)
            }
        }
    }

    // Handle phone number change for OAuth users
    const handlePhoneChange = (e) => {
        const newPhone = e.target.value
        setMobile(newPhone)
        // Reset states when user changes phone number
        setError("")
        setSuccessMessage("")
        setPhoneAlreadyExists(false)
        setOtpSent(false)
        setConfirmationResult(null)
        setOtp("")
        // Reset timer
        if (phoneTimerRef.current) {
            clearInterval(phoneTimerRef.current)
            phoneTimerRef.current = null
        }
        setPhoneResendTimer(0)
        setCanResendPhoneOtp(false)
    }

    const sendOtp = async (e) => {
        e.preventDefault()
        setError("")
        setSuccessMessage("")
        setPhoneAlreadyExists(false)
        setLoading(true)
        const latestMobile = mobile.toString().trim()

        if (!latestMobile) {
            setError("Please enter a valid phone number.")
            setLoading(false)
            return
        }

        if (!validatePhoneNumber(latestMobile)) {
            setError("Please enter a valid 10-digit phone number.")
            setLoading(false)
            return
        }

        const phoneNumber = formatPhoneNumber(latestMobile)

        // Check auth type to determine flow
        if (authType === "form") {
            // For form signup, skip CheckPhoneVerifyAsync and directly send OTP
            try {
                await sendOtpViaFirebase(phoneNumber)
            } catch (err) {
                console.error("Error sending OTP for form signup:", err)
            }
        } else if (authType === "oauth") {
            // For OAuth signup, check if phone exists in database first
            try {
                const result = await dispatch(CheckPhoneVerifyAsync(latestMobile)).unwrap()

                // If phone already exists/verified, show error and allow re-entry
                if (result?.code === 200) {
                    setPhoneAlreadyExists(true)
                    setError(
                        "This phone number is already registered with another account. Please enter a different phone number."
                    )
                    setLoading(false)
                    return
                }
            } catch (err) {
                // Phone not found in database or not verified, proceed with OTP
                console.warn("Phone not verified in database, proceeding with OTP:", err.message)
                setPhoneAlreadyExists(false)
            }

            // Send OTP via Firebase
            try {
                await sendOtpViaFirebase(phoneNumber)
            } catch (err) {
                console.error("Error sending OTP for OAuth signup:", err)
            }
        } else {
            // Default behavior - same as form (skip check)
            try {
                await sendOtpViaFirebase(phoneNumber)
            } catch (err) {
                console.error("Error sending OTP:", err)
            }
        }

        setLoading(false)
    }

    // Resend Phone OTP - FIXED
    const resendPhoneOtp = async (e) => {
        e.preventDefault()
        if (!canResendPhoneOtp) return

        setError("")
        setSuccessMessage("")
        setOtp("")
        setLoading(true)

        const phoneNumber = formatPhoneNumber(mobile.toString().trim())

        // Clear and reinitialize recaptcha for resend
        if (window.recaptchaVerifier) {
            window.recaptchaVerifier.clear()
            window.recaptchaVerifier = null
        }

        try {
            setupRecaptcha()
            await new Promise((resolve) => setTimeout(resolve, 500))
            await sendOtpViaFirebase(phoneNumber)
        } catch (err) {
            console.error("Error resending OTP:", err)
            setError("Error resending OTP. Please try again.")
        }

        setLoading(false)
    }

    const verifyOtp = async (e) => {
        e.preventDefault()
        setError("")
        setSuccessMessage("")
        setLoading(true)

        if (!otp.trim()) {
            setError("Please enter the OTP.")
            setLoading(false)
            return
        }

        if (!confirmationResult) {
            setError("Please request OTP first.")
            setLoading(false)
            return
        }

        try {
            // Verify OTP with Firebase first
            const result = await confirmationResult.confirm(otp.trim())

            // If Firebase verification succeeds, verify with our backend API
            const phoneNumber = formatPhoneNumber(mobile.toString().trim())

            // Send data in correct format: { mobile: "...", code: "..." }
            await dispatch(
                checkPhoneVerificationPublicAsync({
                    mobile: phoneNumber,
                    code: otp.trim(),
                })
            ).unwrap()
        } catch (err) {
            console.error("OTP verification error:", err)
            if (err.code === "auth/invalid-verification-code") {
                setError("Invalid OTP. Please check and try again.")
            } else if (err.code === "auth/code-expired") {
                setError("OTP has expired. Please request a new one.")
            } else {
                setError(err.message || "Invalid OTP. Please try again.")
            }
        } finally {
            setLoading(false)
        }
    }

    // Send email verification code - FIXED
    const sendEmailOtp = async (e) => {
        e.preventDefault()
        setEmailError("")
        setEmailSuccessMessage("")
        setEmailVerifying(true)

        const latestEmail = email.trim()
        if (!latestEmail) {
            setEmailError("Please enter a valid email address.")
            setEmailVerifying(false)
            return
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(latestEmail)) {
            setEmailError("Please enter a valid email format.")
            setEmailVerifying(false)
            return
        }

        try {
            await dispatch(generateEmailVerificationPublicAsync()).unwrap()
            setEmailSuccessMessage("Email verification code sent! Please check your inbox.")
            setEmailOtpSent(true) // Set local state immediately
            startEmailResendTimer() // Start the resend timer
        } catch (error) {
            console.error("Email verification error:", error)
            setEmailError(error?.message || error || "Failed to send verification email.")
        } finally {
            setEmailVerifying(false)
        }
    }

    // Resend Email OTP - FIXED
    const resendEmailOtp = async (e) => {
        e.preventDefault()
        if (!canResendEmailOtp) return

        setEmailError("")
        setEmailSuccessMessage("")
        setEmailOtp("")
        setEmailVerifying(true)

        try {
            // Reset state before resending
            dispatch(resetEmailVerificationState())

            await dispatch(generateEmailVerificationPublicAsync()).unwrap()
            setEmailSuccessMessage("Email verification code resent! Please check your inbox.")
            setEmailOtpSent(true)
            startEmailResendTimer() // Restart the timer
        } catch (error) {
            console.error("Resend email error:", error)
            setEmailError(error?.message || error || "Failed to resend verification email.")
        } finally {
            setEmailVerifying(false)
        }
    }

    // Verify email OTP - FIXED
    const verifyEmailOtp = async (e) => {
        e.preventDefault()
        setEmailError("")
        setEmailSuccessMessage("")

        if (!emailOtp || emailOtp.length !== 6) {
            setEmailError("Please enter a valid 6-digit OTP.")
            return
        }

        setEmailVerifying(true)

        try {
            await dispatch(checkEmailVerificationPublicAsync(emailOtp)).unwrap()
            // Success will be handled by useEffect watching emailVerifiedRedux
        } catch (error) {
            console.error("Email OTP verification error:", error)
            setEmailError(error?.message || error || "Invalid OTP. Please try again.")
        } finally {
            setEmailVerifying(false)
        }
    }

    const handleSkip = () => {
        navigate(redirectTo, { replace: true })
    }

    const getProgressValue = () => {
        let progress = 0
        if (isPhoneVerified) progress += 50
        if (isEmailVerified) progress += 50
        return progress
    }

    // Determine if phone field should be editable
    const isPhoneFieldDisabled = () => {
        if (authType === "form") return true // Form users cannot edit phone
        if (loading) return true // Disable during loading
        if (otpSent && !phoneAlreadyExists) return true // Disable after OTP sent (but not if phone exists error)
        if (isPhoneVerified) return true // Disable if already verified
        return false // OAuth users can edit
    }

    const isPhoneFieldReadOnly = () => {
        return authType === "form" // Only form users have readonly
    }

    // Show loading state while fetching auth type
    if (!isAuthTypeLoaded) {
        return (
            <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center" p={4}>
                <VStack spacing={4}>
                    <Spinner size="xl" color="blue.500" thickness="4px" speed="0.65s" />
                    <Text color={mutedTextColor}>Loading...</Text>
                </VStack>
            </Box>
        )
    }

    return (
        <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center" p={4}>
            <Container maxW="4xl">
                <HStack spacing={8} align="stretch" minH="80vh">
                    {/* Left Side - Illustration */}
                    <VStack
                        flex="1"
                        justify="center"
                        align="center"
                        spacing={6}
                        display={{ base: "none", lg: "flex" }}
                        px={8}
                    >
                        <Circle size="120px" bg="blue.100" color="blue.600">
                            <ShieldIcon w={16} h={16} />
                        </Circle>
                        <VStack spacing={3} textAlign="center">
                            <Heading size="lg" color={textColor} fontWeight="600">
                                Secure Verification
                            </Heading>
                            <Text fontSize="sm" color={mutedTextColor} maxW="xs" lineHeight="1.6">
                                We use industry-standard security measures to protect your personal information during
                                verification.
                            </Text>
                        </VStack>
                        <VStack spacing={2} w="full" maxW="xs">
                            <HStack w="full" justify="space-between">
                                <Text fontSize="xs" color={mutedTextColor}>
                                    Security Level
                                </Text>
                                <Text fontSize="xs" color={mutedTextColor}>
                                    {getProgressValue() > 0 ? "High" : "Standard"}
                                </Text>
                            </HStack>
                            <Progress
                                value={getProgressValue() || 25}
                                size="sm"
                                borderRadius="full"
                                colorScheme="blue"
                                bg="gray.200"
                                w="full"
                            />
                        </VStack>
                        {authType && (
                            <Text fontSize="xs" color={mutedTextColor}>
                                Signed up via:{" "}
                                <Text as="span" fontWeight="medium" textTransform="capitalize">
                                    {authType === "oauth" ? "Google OAuth" : "Form"}
                                </Text>
                            </Text>
                        )}
                    </VStack>

                    {/* Right Side - Form */}
                    <Card
                        flex="1"
                        bg={cardBg}
                        shadow="xl"
                        borderRadius="2xl"
                        border="1px"
                        borderColor={borderColor}
                        maxW={{ base: "full", lg: "lg" }}
                        overflow="hidden"
                    >
                        {/* Header with gradient */}
                        <Box bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" px={6} py={5}>
                            <VStack spacing={1} align="start">
                                <Heading size="md" color="white" fontWeight="600">
                                    Verify Phone & Email
                                </Heading>
                                <Text fontSize="sm" color="whiteAlpha.800">
                                    Securely verify your phone number and email address
                                </Text>
                            </VStack>
                        </Box>

                        <CardBody pt={6} px={6} pb={6}>
                            <VStack spacing={6}>
                                {/* Progress indicator */}
                                {(isPhoneVerified || isEmailVerified) && (
                                    <Box w="full">
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontSize="xs" color={mutedTextColor}>
                                                Verification Progress
                                            </Text>
                                            <Text fontSize="xs" fontWeight="medium" color="blue.600">
                                                {getProgressValue()}% Complete
                                            </Text>
                                        </HStack>
                                        <Progress
                                            value={getProgressValue()}
                                            size="sm"
                                            borderRadius="full"
                                            colorScheme="blue"
                                            bg="gray.100"
                                        />
                                    </Box>
                                )}

                                {/* Email Verification Section */}
                                <Box w="full">
                                    <HStack mb={3} justify="space-between" align="center">
                                        <HStack spacing={2}>
                                            <Circle
                                                size="36px"
                                                bg={isEmailVerified ? "green.100" : "gray.100"}
                                                color={isEmailVerified ? "green.600" : mutedTextColor}
                                            >
                                                <Icon as={isEmailVerified ? CheckIcon : EmailIcon} w={4} h={4} />
                                            </Circle>
                                            <Text fontWeight="500" color={textColor} fontSize="sm">
                                                Email Address
                                            </Text>
                                        </HStack>
                                        {isEmailVerified && (
                                            <Badge colorScheme="green" variant="subtle" borderRadius="full" fontSize="xs" px={2}>
                                                <HStack spacing={1} alignItems={"start"}>
                                                    <CheckIcon w={3} h={3} />
                                                    <div className="font-normal">Verified</div>
                                                </HStack>
                                            </Badge>
                                        )}
                                    </HStack>

                                    <VStack spacing={3}>
                                        <FormControl>
                                            <InputGroup size="md">
                                                <InputLeftElement pointerEvents="none">
                                                    <EmailIcon color={mutedTextColor} w={4} h={4} />
                                                </InputLeftElement>
                                                <Input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="Enter your email address"
                                                    borderColor={borderColor}
                                                    _hover={{ borderColor: "gray.300" }}
                                                    _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px #4299e1" }}
                                                    readOnly={true}
                                                    isDisabled={true}
                                                    bg="gray.50"
                                                    fontSize="sm"
                                                />
                                            </InputGroup>
                                            <Text fontSize="xs" color={mutedTextColor} mt={1}>
                                                Email address from your registration (cannot be changed)
                                            </Text>
                                        </FormControl>

                                        <Button
                                            colorScheme={isEmailVerified ? "gray" : "blue"}
                                            variant={isEmailVerified ? "outline" : "solid"}
                                            size="md"
                                            w="full"
                                            onClick={sendEmailOtp}
                                            isLoading={emailVerifying && !emailOtpSent}
                                            loadingText="Sending Code..."
                                            isDisabled={isEmailVerified || emailOtpSent || !email}
                                        >
                                            {isEmailVerified
                                                ? "Verified"
                                                : emailOtpSent
                                                    ? "Code Sent"
                                                    : "Send Verification Code"}
                                        </Button>

                                        {/* Email OTP Input Section - FIXED */}
                                        {emailOtpSent && !isEmailVerified && (
                                            <>
                                                <Divider />
                                                <Text fontSize="sm" color={mutedTextColor} textAlign="center">
                                                    Enter verification code
                                                </Text>

                                                <Input
                                                    type="text"
                                                    value={emailOtp}
                                                    onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                    placeholder="Enter the 6-digit OTP"
                                                    borderColor={borderColor}
                                                    _hover={{ borderColor: "gray.300" }}
                                                    _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px #4299e1" }}
                                                    fontSize="sm"
                                                    textAlign="center"
                                                    size="md"
                                                    maxLength={6}
                                                    letterSpacing="0.3em"
                                                    fontWeight="bold"
                                                    isDisabled={emailVerifying}
                                                />

                                                {/* Email Resend OTP Button - FIXED */}
                                                <HStack w="full" justify="space-between" align="center">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        leftIcon={<RefreshIcon w={4} h={4} />}
                                                        onClick={resendEmailOtp}
                                                        isDisabled={!canResendEmailOtp || emailVerifying}
                                                        color={canResendEmailOtp && !emailVerifying ? "blue.500" : "gray.400"}
                                                        _hover={{ bg: canResendEmailOtp ? "blue.50" : "transparent" }}
                                                        px={2}
                                                    >
                                                        {canResendEmailOtp ? "Resend OTP" : `Resend in ${formatTimer(emailResendTimer)}`}
                                                    </Button>
                                                    {emailResendTimer > 0 && (
                                                        <Text fontSize="xs" color={mutedTextColor}>
                                                            Please wait before requesting a new code
                                                        </Text>
                                                    )}
                                                </HStack>

                                                <Button
                                                    colorScheme="green"
                                                    size="md"
                                                    w="full"
                                                    onClick={verifyEmailOtp}
                                                    isLoading={emailVerifying}
                                                    loadingText="Verifying..."
                                                    isDisabled={!emailOtp || emailOtp.length !== 6}
                                                >
                                                    Verify Email OTP
                                                </Button>
                                            </>
                                        )}

                                        {emailError && (
                                            <Alert status="error" borderRadius="md" py={2}>
                                                <AlertIcon />
                                                <Text fontSize="sm">{emailError}</Text>
                                            </Alert>
                                        )}

                                        {emailSuccessMessage && (
                                            <Alert status="success" borderRadius="md" py={2}>
                                                <AlertIcon />
                                                <Text fontSize="sm">{emailSuccessMessage}</Text>
                                            </Alert>
                                        )}
                                    </VStack>
                                </Box>

                                {/* Divider */}
                                <HStack w="full" align="center">
                                    <Divider />
                                    <Text fontSize="sm" color={mutedTextColor} px={3} whiteSpace="nowrap">
                                        And
                                    </Text>
                                    <Divider />
                                </HStack>

                                {/* Phone Verification Section */}
                                <Box w="full">
                                    <HStack mb={3} justify="space-between" align="center">
                                        <HStack spacing={2}>
                                            <Circle
                                                size="36px"
                                                bg={
                                                    isPhoneVerified
                                                        ? "green.100"
                                                        : phoneAlreadyExists
                                                            ? "red.100"
                                                            : "gray.100"
                                                }
                                                color={
                                                    isPhoneVerified
                                                        ? "green.600"
                                                        : phoneAlreadyExists
                                                            ? "red.600"
                                                            : mutedTextColor
                                                }
                                            >
                                                <Icon as={isPhoneVerified ? CheckIcon : PhoneIcon} w={4} h={4} />
                                            </Circle>
                                            <Text fontWeight="500" color={textColor} fontSize="sm">
                                                Phone Number
                                            </Text>
                                            {authType === "oauth" && (
                                                <Text fontSize="xs" color="blue.500" ml={1}>
                                                    (Enter your phone number)
                                                </Text>
                                            )}
                                        </HStack>
                                        {isPhoneVerified && (
                                            <Badge colorScheme="green" variant="subtle" borderRadius="full" fontSize="xs" px={2}>
                                                <HStack spacing={1} alignItems={"start"}>
                                                    <CheckIcon w={3} h={3} />
                                                    <div className="font-normal">Verified</div>
                                                </HStack>
                                            </Badge>
                                        )}
                                    </HStack>

                                    <VStack spacing={3}>
                                        <FormControl>
                                            <InputGroup size="md">
                                                <InputLeftElement pointerEvents="none">
                                                    <PhoneIcon color={mutedTextColor} w={4} h={4} />
                                                </InputLeftElement>
                                                <Input
                                                    type="tel"
                                                    value={mobile}
                                                    onChange={authType === "oauth" ? handlePhoneChange : undefined}
                                                    placeholder={
                                                        authType === "oauth"
                                                            ? "Enter your phone number (e.g. 9876543210)"
                                                            : "Phone number from registration"
                                                    }
                                                    borderColor={phoneAlreadyExists ? "red.500" : borderColor}
                                                    _hover={{ borderColor: phoneAlreadyExists ? "red.500" : "gray.300" }}
                                                    _focus={{
                                                        borderColor: phoneAlreadyExists ? "red.500" : "blue.400",
                                                        boxShadow: phoneAlreadyExists
                                                            ? "0 0 0 1px #E53E3E"
                                                            : "0 0 0 1px #4299e1",
                                                    }}
                                                    readOnly={isPhoneFieldReadOnly()}
                                                    isDisabled={isPhoneFieldDisabled()}
                                                    bg={isPhoneFieldDisabled() ? "gray.50" : "white"}
                                                    fontSize="sm"
                                                />
                                            </InputGroup>
                                            <Text
                                                fontSize="xs"
                                                color={phoneAlreadyExists ? "red.500" : mutedTextColor}
                                                mt={1}
                                            >
                                                {authType === "form"
                                                    ? "Phone number from your registration (cannot be changed)"
                                                    : phoneAlreadyExists
                                                        ? "This number is already registered. Please enter a different number."
                                                        : "Enter your 10-digit mobile number without country code"}
                                            </Text>
                                        </FormControl>

                                        {/* Phone Already Exists Warning */}
                                        {phoneAlreadyExists && (
                                            <Alert status="warning" borderRadius="md" py={3}>
                                                <AlertIcon as={WarningIcon} />
                                                <Box>
                                                    <AlertTitle fontSize="sm">Phone Number Already Registered</AlertTitle>
                                                    <AlertDescription fontSize="xs">
                                                        This phone number is already associated with another account. Please
                                                        enter a different phone number to continue.
                                                    </AlertDescription>
                                                </Box>
                                            </Alert>
                                        )}

                                        <Button
                                            colorScheme={
                                                isPhoneVerified ? "gray" : phoneAlreadyExists ? "orange" : "blue"
                                            }
                                            variant={isPhoneVerified ? "outline" : "solid"}
                                            size="md"
                                            w="full"
                                            onClick={sendOtp}
                                            isLoading={loading}
                                            loadingText={
                                                authType === "oauth" ? "Checking & Sending OTP..." : "Sending OTP..."
                                            }
                                            isDisabled={
                                                loading ||
                                                (otpSent && !phoneAlreadyExists) ||
                                                isPhoneVerified ||
                                                !mobile.toString().trim()
                                            }
                                        >
                                            {isPhoneVerified
                                                ? "Verified"
                                                : otpSent && !phoneAlreadyExists
                                                    ? "OTP Sent"
                                                    : phoneAlreadyExists
                                                        ? "Try Different Number"
                                                        : "Send Verification Code"}
                                        </Button>

                                        {otpSent && !isPhoneVerified && !phoneAlreadyExists && (
                                            <>
                                                <Divider />
                                                <Text fontSize="sm" color={mutedTextColor} textAlign="center">
                                                    Enter verification code
                                                </Text>

                                                <Input
                                                    type="text"
                                                    value={otp}
                                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                    placeholder="Enter the 6-digit OTP"
                                                    borderColor={borderColor}
                                                    _hover={{ borderColor: "gray.300" }}
                                                    _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px #4299e1" }}
                                                    fontSize="sm"
                                                    textAlign="center"
                                                    size="md"
                                                    maxLength={6}
                                                    letterSpacing="0.3em"
                                                    fontWeight="bold"
                                                    isDisabled={loading}
                                                />

                                                {/* Phone Resend OTP Button - FIXED */}
                                                <HStack w="full" justify="space-between" align="center">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        leftIcon={<RefreshIcon w={4} h={4} />}
                                                        onClick={resendPhoneOtp}
                                                        isDisabled={!canResendPhoneOtp || loading}
                                                        color={canResendPhoneOtp && !loading ? "blue.500" : "gray.400"}
                                                        _hover={{ bg: canResendPhoneOtp ? "blue.50" : "transparent" }}
                                                        px={2}
                                                    >
                                                        {canResendPhoneOtp
                                                            ? "Resend OTP"
                                                            : `Resend in ${formatTimer(phoneResendTimer)}`}
                                                    </Button>
                                                    {phoneResendTimer > 0 && (
                                                        <Text fontSize="xs" color={mutedTextColor}>
                                                            Please wait before requesting a new code
                                                        </Text>
                                                    )}
                                                </HStack>

                                                <Button
                                                    colorScheme="green"
                                                    size="md"
                                                    w="full"
                                                    onClick={verifyOtp}
                                                    isLoading={loading}
                                                    loadingText="Verifying..."
                                                    isDisabled={!otp || otp.length !== 6}
                                                >
                                                    Verify OTP
                                                </Button>
                                            </>
                                        )}

                                        {error && !phoneAlreadyExists && (
                                            <Alert status="error" borderRadius="md" py={2}>
                                                <AlertIcon />
                                                <Text fontSize="sm">{error}</Text>
                                            </Alert>
                                        )}

                                        {successMessage && (
                                            <Alert status="success" borderRadius="md" py={2}>
                                                <AlertIcon />
                                                <Text fontSize="sm">{successMessage}</Text>
                                            </Alert>
                                        )}
                                    </VStack>
                                </Box>

                                {/* Skip Section */}
                                <Divider />

                                <Button
                                    variant="ghost"
                                    colorScheme="gray"
                                    size="md"
                                    w="full"
                                    onClick={handleSkip}
                                    rightIcon={<ArrowRightIcon w={4} h={4} />}
                                >
                                    Skip for now
                                </Button>

                                <Text fontSize="xs" color={mutedTextColor} textAlign="center" lineHeight="1.5">
                                    By continuing, you agree to our{" "}
                                    <Link href="#" color="blue.500" textDecoration="underline">
                                        Terms of Service
                                    </Link>{" "}
                                    and{" "}
                                    <Link href="/privacy-policy" color="blue.500" textDecoration="underline">
                                        Privacy Policy
                                    </Link>
                                    .
                                </Text>
                            </VStack>
                        </CardBody>
                    </Card>
                </HStack>
            </Container>
            <div id="recaptcha-container" style={{ marginTop: "20px" }}></div>
        </Box>
    )
}